import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FaChevronDown, FaSearch } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import LabelBadge from 'components/labels/LabelBadge';
import { showErrorAlert } from 'utils/AlertUtils';
import * as labelApi from 'utils/labelApi';
import { canAccessLabels } from 'utils/StaticData';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Searchable label multi-select (RAT-1689/1690).
 *
 * Two feeds, one component:
 *  - with `merchantId`  -> only what that merchant may carry (Global + its own
 *    region), most-used first. Used by the Edit Merchant form.
 *  - without            -> every label in the caller's scope. Used by the
 *    merchant list's label filter.
 *
 * Search runs server-side and results are paginated, so the picker never pulls
 * the whole taxonomy down. Chips are rendered from `initialLabels` merged with
 * whatever has been fetched, so a selected label off page 1 still shows a proper
 * chip rather than a bare id.
 *
 * Renders nothing for the roles labels do not exist for (Merchant, Organization):
 * the label endpoints 403 them, so opening the picker could only ever produce an
 * error. Checked here as well as at the call sites, because this component is
 * reached from several screens those roles can still open (coupons, stations,
 * portal users) and one missed wrapper would otherwise expose it again.
 */
const LabelMultiSelect = ({
  merchantId,
  value = [],
  onChange,
  initialLabels = [],
  isDisabled = false,
  placeholder = 'Select labels',
  buttonWidth = '100%',
  showSelectedChips = true,
}) => {
  const { adminDetails } = useSelector((state) => state.authSlice);
  const canUseLabels = canAccessLabels(adminDetails?.role);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);

  // Every label object seen so far, so a chip can be drawn for any selected id
  // even after the option list has been filtered away by a search.
  const [knownLabels, setKnownLabels] = useState(() => {
    const map = {};
    (initialLabels || []).forEach((label) => {
      map[label.id] = label;
    });
    return map;
  });

  useEffect(() => {
    if (!initialLabels?.length) return;
    setKnownLabels((prev) => {
      const next = { ...prev };
      initialLabels.forEach((label) => {
        next[label.id] = label;
      });
      return next;
    });
  }, [initialLabels]);

  useEffect(() => {
    debounceRef.current = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchPage = useCallback(
    async (targetPage) => {
      setIsLoading(true);
      try {
        const query = {
          search: debouncedSearch,
          page: targetPage,
          limit: PAGE_SIZE,
        };
        const data = merchantId
          ? await labelApi.getAssignableLabels(merchantId, query)
          : await labelApi.listLabels({
              ...query,
              sortKey: 'merchantCount',
              sortBy: -1,
            });
        const list = data?.list || [];
        setTotal(data?.total || 0);
        setOptions((prev) => (targetPage === 1 ? list : [...prev, ...list]));
        setKnownLabels((prev) => {
          const next = { ...prev };
          list.forEach((label) => {
            next[label.id] = label;
          });
          return next;
        });
      } catch (error) {
        showErrorAlert(
          error?.message || error?.error?.message || 'Failed to load labels',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [debouncedSearch, merchantId],
  );

  // Only fetch once the picker has been opened - a merchant form has many
  // fields and none of them should pay for a list the user may never open.
  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    fetchPage(1);
  }, [isOpen, fetchPage]);

  const selectedLabels = useMemo(
    () => value.map((id) => knownLabels[id]).filter(Boolean),
    [value, knownLabels],
  );

  const toggle = (label) => {
    const next = value.includes(label.id)
      ? value.filter((id) => id !== label.id)
      : [...value, label.id];
    onChange(next);
  };

  const remove = (label) => onChange(value.filter((id) => id !== label.id));

  const hasMore = options.length < total;
  const triggerText = value.length
    ? `${value.length} label${value.length > 1 ? 's' : ''} selected`
    : placeholder;

  // After the hooks, never before - an early return above them would change the
  // hook order between roles.
  if (!canUseLabels) return null;

  return (
    <Box>
      <Popover
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        placement="bottom-start"
      >
        <PopoverTrigger>
          {/* Styled to sit next to the plain Chakra Selects in the merchant
              list toolbar - the default outline Button borrows its border from
              the text colour, which read darker and shorter than the filters
              beside it. Matches their white fill, 10px radius and 40px height. */}
          <Button
            w={buttonWidth}
            h="40px"
            justifyContent="space-between"
            rightIcon={<FaChevronDown size="12px" color="#718096" />}
            variant="unstyled"
            display="flex"
            px="16px"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="10px"
            fontWeight="400"
            fontSize="md"
            textAlign="left"
            _hover={{ borderColor: 'gray.300' }}
            _focusVisible={{
              borderColor: 'blue.400',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
            }}
            isDisabled={isDisabled}
            color={value.length ? 'gray.800' : 'gray.600'}
          >
            {triggerText}
          </Button>
        </PopoverTrigger>
        {/* Explicit width rather than matchWidth: the filter trigger is only
            200px wide, and matching that squeezed the chip, scope and usage
            count into each other. */}
        <PopoverContent minW="340px" w="max-content" maxW="440px">
          <PopoverBody px="0" py="2">
            <Box px="3" pb="2">
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <FaSearch color="gray" size="12px" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name or ID (e.g. LBL-001)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  borderRadius="md"
                />
              </InputGroup>
            </Box>

            <VStack
              align="stretch"
              spacing="0"
              maxH="260px"
              overflowY="auto"
              px="3"
            >
              {isLoading && options.length === 0 && (
                <Flex justify="center" py="4">
                  <Spinner size="sm" />
                </Flex>
              )}

              {!isLoading && options.length === 0 && (
                <Text py="3" fontSize="sm" color="gray.500">
                  {debouncedSearch
                    ? 'No labels match your search'
                    : 'No labels available'}
                </Text>
              )}

              {options.map((label) => (
                <Flex
                  key={label.id}
                  align="center"
                  justify="space-between"
                  py="6px"
                  gap="2"
                >
                  <Checkbox
                    isChecked={value.includes(label.id)}
                    onChange={() => toggle(label)}
                    flex="1"
                    minW="0"
                  >
                    <Flex align="center" gap="2" minW="0">
                      <LabelBadge label={label} size="sm" />
                      <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                        {label.regionName}
                      </Text>
                    </Flex>
                  </Checkbox>
                </Flex>
              ))}

              {hasMore && (
                <Button
                  size="xs"
                  variant="ghost"
                  isLoading={isLoading}
                  onClick={() => {
                    const next = page + 1;
                    setPage(next);
                    fetchPage(next);
                  }}
                  my="2"
                >
                  Load more ({total - options.length} remaining)
                </Button>
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      {showSelectedChips && selectedLabels.length > 0 && (
        <Flex mt="2" gap="6px" wrap="wrap">
          {selectedLabels.map((label) => (
            <LabelBadge
              key={label.id}
              label={label}
              size="sm"
              onRemove={isDisabled ? undefined : remove}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

LabelMultiSelect.propTypes = {
  // When set, only labels assignable to this merchant are offered.
  merchantId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  // Label objects for ids already selected, so their chips render before the
  // option list has been fetched.
  initialLabels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      color: PropTypes.string,
    }),
  ),
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  buttonWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  showSelectedChips: PropTypes.bool,
};

export default LabelMultiSelect;
